import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Button,
  Space,
  Card,
  Tag,
  Table,
  App,
  Input,
  Select,
  Switch,
  Drawer,
  Modal,
  Popconfirm,
  DatePicker,
} from 'antd';
import { ArrowLeftOutlined, EyeOutlined, LinkOutlined, DisconnectOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AssetCategory,
  AssetListItem,
  ISSUER_ASSET_STATUS_OVERRIDES,
  IssuerAssetListItem,
  IssuerAssetStatusOverride,
  IssuerKey,
} from '@mygate/common';
import { issuersService } from '@/services/issuers';
import { assetManagementService } from '@/services/asset-management';
import { IssuerAssetPreview } from '@/components/IssuerAssetPreview';
import { issuerStatusColor, mirrorWordDiffers } from '@/utils/issuerStatus';

const { Title, Text } = Typography;

/** What each declarable state means to the person choosing it. */
const STATUS_LABELS: Record<IssuerAssetStatusOverride, string> = {
  soon: 'soon — announced, not for sale',
  open: 'open — for sale',
  closed: 'closed',
};

/** A declaration whose end has passed no longer decides anything; the dates do (MYG-898). */
function declarationLapsed(row: IssuerAssetListItem): boolean {
  return !!row.statusOverrideUntil && dayjs(row.statusOverrideUntil).valueOf() <= Date.now();
}

function isIssuerKey(value: string | undefined): value is IssuerKey {
  return !!value && (Object.values(IssuerKey) as string[]).includes(value);
}

/** One issuer's mirrored catalog: what they publish, what it is linked to, and the link controls. */
export function IssuerAssetsPage() {
  const navigate = useNavigate();
  const params = useParams<{ issuer: string }>();
  const issuer = isIssuerKey(params.issuer) ? params.issuer : undefined;
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();

  const [search, setSearch] = useState('');
  const [linkedFilter, setLinkedFilter] = useState<'all' | 'linked' | 'unlinked'>('all');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [preview, setPreview] = useState<IssuerAssetListItem | null>(null);
  const [linkTarget, setLinkTarget] = useState<IssuerAssetListItem | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | undefined>();

  const queryKey = ['issuer-assets', issuer, search, linkedFilter, includeInactive];
  const { data, isLoading } = useQuery({
    queryKey,
    enabled: !!issuer,
    queryFn: async () =>
      (
        await issuersService.listIssuerAssets(issuer as IssuerKey, {
          search: search || undefined,
          linked: linkedFilter === 'all' ? undefined : linkedFilter === 'linked',
          includeInactive,
        })
      ).data,
    staleTime: 1000 * 30,
  });

  const { data: rwaAssets } = useQuery({
    queryKey: ['assets', 'rwa-link-candidates'],
    enabled: linkTarget !== null,
    queryFn: async () => (await assetManagementService.listAssets({ category: AssetCategory.RWA, limit: 100 })).data,
  });
  /**
   * Assets this issuer has not claimed yet. Derived from the issuer side rather than from a field on
   * the asset: the public asset shape deliberately does not carry `issuerLink` (it names an internal
   * record and the staff member who linked it), and the server refuses a double-link anyway.
   */
  const candidates = useMemo(() => {
    const linkedIds = new Set((data?.assets ?? []).map(row => row.linkedAsset?._id).filter(Boolean));
    return ((rwaAssets?.assets ?? []) as AssetListItem[]).filter(asset => !linkedIds.has(asset._id));
  }, [rwaAssets, data]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['issuer-assets'] });
    queryClient.invalidateQueries({ queryKey: ['issuers'] });
    queryClient.invalidateQueries({ queryKey: ['assets'] });
  };

  const linkMutation = useMutation({
    mutationFn: ({ issuerAssetId, assetId }: { issuerAssetId: string; assetId: string }) =>
      issuersService.linkAsset(issuer as IssuerKey, issuerAssetId, assetId),
    onSuccess: response => {
      message.success(`Linked to ${response.data?.symbol ?? 'asset'}`);
      setLinkTarget(null);
      setSelectedAssetId(undefined);
      invalidate();
    },
    onError: error => message.error(error instanceof Error ? error.message : 'Failed to link'),
  });

  const statusMutation = useMutation({
    mutationFn: ({
      issuerAssetId,
      status,
      until,
    }: {
      issuerAssetId: string;
      status: IssuerAssetStatusOverride | null;
      until: string | null;
    }) => issuersService.setAssetStatus(issuer as IssuerKey, issuerAssetId, status, until),
    onSuccess: response => {
      const declared = response.data?.statusOverride;
      message.success(declared ? `Status set to "${declared}"` : 'Status back to the subscription window');
      invalidate();
    },
    onError: error => message.error(error instanceof Error ? error.message : 'Failed to set the status'),
  });

  const unlinkMutation = useMutation({
    mutationFn: (issuerAssetId: string) => issuersService.unlinkAsset(issuer as IssuerKey, issuerAssetId),
    onSuccess: response => {
      message.success(response.data?.message ?? 'Unlinked');
      invalidate();
    },
    onError: error => message.error(error instanceof Error ? error.message : 'Failed to unlink'),
  });

  /**
   * Declare a state, with an optional end (MYG-898), or hand the tranche back to its dates. Without an
   * end the declaration outranks the dates until somebody clears it, and a "soon" keeps hiding the call
   * to action after the window opens.
   */
  const confirmDeclaration = (row: IssuerAssetListItem, status: IssuerAssetStatusOverride | null) => {
    let until: Dayjs | null = null;
    modal.confirm({
      title: status ? `Declare ${row.externalId} "${status}"?` : `Hand ${row.externalId} back to its dates?`,
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            This is what the app shows and what the order gate answers — it decides whether the tranche can be bought.
          </Text>
          {status && (
            <>
              <Text type="secondary">
                Ends on (optional). From then on the subscription dates decide again. For &quot;soon&quot;, this is the
                announced opening.
              </Text>
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="DD.MM.YYYY HH:mm"
                style={{ width: '100%' }}
                // With showTime the picker otherwise waits for its own OK, and a value picked before
                // pressing "Declare" is dropped — a "soon" stored with no end (MYG-898 review).
                needConfirm={false}
                disabledDate={day => day.isBefore(dayjs(), 'day')}
                onChange={value => {
                  until = value;
                }}
              />
            </>
          )}
        </Space>
      ),
      okText: status ? 'Declare' : 'Clear',
      onOk: () => {
        // A time earlier today passes the day filter; the server would refuse it after the dialog closed.
        if (status && until && !until.isAfter(dayjs())) {
          message.error('The end must be in the future');
          return Promise.reject(new Error('end in the past'));
        }
        statusMutation.mutate({ issuerAssetId: row._id, status, until: status && until ? until.toISOString() : null });
        return undefined;
      },
    });
  };

  const columns: ColumnsType<IssuerAssetListItem> = [
    {
      title: 'Name',
      key: 'name',
      width: 240,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ opacity: row.active ? 1 : 0.5 }}>
            {row.name}
          </Text>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
            {row.externalId}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 190,
      /*
       * What the app will serve, and where it came from. A declared state wins over the one the
       * subscription window implies, and it is the only way to say "announced, not open yet" at
       * all — the venue stamps `availableFrom` at the moment it is asked, so no tranche's dates
       * ever place it before its own window (MYG-897). Setting it here also decides whether the
       * order gate accepts a purchase, which is why it is worth the width.
       */
      render: (_, row) => (
        <Space size={4} direction="vertical" style={{ width: '100%' }}>
          <Space size={4}>
            {/* The state the app and the order gate serve, not the word the mirror stored: since MYG-899
                the two differ whenever the dates or a declaration decide, and the served one is what a
                customer is refused by (MYG-902). The mirror's word follows when it says something else. */}
            <Tag color={issuerStatusColor(row.servedStatus)}>{row.servedStatus}</Tag>
            {row.statusOverride && !declarationLapsed(row) && <Tag>declared</Tag>}
            {row.statusOverride && declarationLapsed(row) && <Tag>declaration ended — dates decide</Tag>}
            {mirrorWordDiffers(row.status, row.servedStatus) && <Tag>mirror: {row.status}</Tag>}
            {!row.active && <Tag color="red">unpublished</Tag>}
          </Space>
          <Select
            size="small"
            style={{ width: '100%' }}
            // Truthiness, the same test the server applies, so a stored empty string reads as
            // "nobody has said" on both sides rather than as a state with no name. Only a declaration in
            // force: an ended one would sit in the Select, and picking the same option again fires no
            // change, so it could not be declared again (MYG-898 review).
            value={row.statusOverride && !declarationLapsed(row) ? row.statusOverride : null}
            onChange={status => confirmDeclaration(row, status)}
            // Per row: one shared flag would spin every Select on the page for one change.
            loading={statusMutation.isPending && statusMutation.variables?.issuerAssetId === row._id}
            options={[
              // What clearing the declaration would serve, not the mirror's word: the two differ exactly
              // when it matters (MYG-902 review).
              { value: null, label: `From the window (${row.windowStatus})` },
              ...ISSUER_ASSET_STATUS_OVERRIDES.map(value => ({ value, label: STATUS_LABELS[value] })),
            ]}
          />
          {row.statusOverride && !declarationLapsed(row) && (
            <Space size={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.statusOverrideUntil
                  ? `until ${dayjs(row.statusOverrideUntil).format('DD.MM.YYYY HH:mm')}`
                  : 'no end date'}
              </Text>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, fontSize: 12 }}
                onClick={() => confirmDeclaration(row, row.statusOverride ?? null)}
              >
                Change end
              </Button>
            </Space>
          )}
        </Space>
      ),
    },
    {
      title: 'Token',
      key: 'token',
      width: 130,
      render: (_, row) =>
        row.tokenSymbol ? (
          <Text style={{ fontFamily: 'monospace' }}>{row.tokenSymbol}</Text>
        ) : (
          <Text type="warning">not set</Text>
        ),
    },
    {
      title: 'Price',
      key: 'price',
      align: 'right',
      width: 100,
      render: (_, row) => (row.tokenPriceEur !== undefined ? `${row.tokenPriceEur} EUR` : '—'),
    },
    {
      title: 'Content',
      key: 'content',
      width: 180,
      render: (_, row) => (
        <Space size={4} wrap>
          {row.locales.map(l => (
            <Tag key={l}>{l.toUpperCase()}</Tag>
          ))}
          <Text type="secondary">
            {row.media.length} img · {row.documents.length} docs
          </Text>
        </Space>
      ),
    },
    {
      title: 'Linked asset',
      key: 'linked',
      width: 160,
      render: (_, row) =>
        row.linkedAsset ? (
          <Tag
            color={row.linkedAsset.isActive ? 'green' : 'purple'}
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/assets/${row.linkedAsset?._id}`)}
          >
            {row.linkedAsset.symbol}
            {row.linkedAsset.isActive ? '' : ' (inactive)'}
          </Tag>
        ) : (
          <Tag color="orange">unlinked</Tag>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, row) => (
        <Space wrap>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setPreview(row)}>
            Preview
          </Button>
          {row.linkedAsset ? (
            <Popconfirm
              title={`Unlink from ${row.linkedAsset.symbol}?`}
              onConfirm={() => unlinkMutation.mutate(row._id)}
            >
              <Button size="small" danger icon={<DisconnectOutlined />}>
                Unlink
              </Button>
            </Popconfirm>
          ) : (
            <Button size="small" type="primary" icon={<LinkOutlined />} onClick={() => setLinkTarget(row)}>
              Link…
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="page-toolbar">
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/issuers')} type="text" />
          <Title level={4} style={{ margin: 0 }}>
            {issuer ?? 'Unknown issuer'} catalog
          </Title>
          <Text type="secondary">mirrored issuer records and their asset links</Text>
        </Space>
      </div>
      <>
        {!issuer ? (
          <Card>Unknown issuer “{params.issuer}”.</Card>
        ) : (
          <Card>
            <Space style={{ marginBottom: 16 }} wrap>
              <Input
                placeholder="Search name, token, id…"
                prefix={<SearchOutlined />}
                value={search}
                onChange={e => setSearch(e.target.value)}
                allowClear
                style={{ width: 280 }}
              />
              <Select
                value={linkedFilter}
                onChange={setLinkedFilter}
                style={{ width: 160 }}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'linked', label: 'Linked' },
                  { value: 'unlinked', label: 'Needs linking' },
                ]}
              />
              <Space wrap>
                <Switch checked={includeInactive} onChange={setIncludeInactive} size="small" />
                <Text type="secondary">show unpublished</Text>
              </Space>
            </Space>
            <Table
              tableLayout="fixed"
              scroll={{ x: 1110 }}
              columns={columns}
              dataSource={data?.assets ?? []}
              rowKey="_id"
              loading={isLoading}
              pagination={false}
            />
          </Card>
        )}
      </>

      <Drawer title={preview?.name} open={preview !== null} onClose={() => setPreview(null)} width={760}>
        {preview && <IssuerAssetPreview issuerAsset={preview} />}
      </Drawer>

      <Modal
        title={`Link “${linkTarget?.name}” to an RWA asset`}
        open={linkTarget !== null}
        onCancel={() => {
          setLinkTarget(null);
          setSelectedAssetId(undefined);
        }}
        onOk={() =>
          linkTarget &&
          selectedAssetId &&
          linkMutation.mutate({ issuerAssetId: linkTarget._id, assetId: selectedAssetId })
        }
        okButtonProps={{ disabled: !selectedAssetId, loading: linkMutation.isPending }}
        okText="Link"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">
            Only RWA assets without an issuer link are offered. Import the Tradevest token first if it is missing.
          </Text>
          <Select
            showSearch
            placeholder="Choose the asset that trades this token"
            style={{ width: '100%' }}
            value={selectedAssetId}
            onChange={setSelectedAssetId}
            optionFilterProp="label"
            options={candidates.map(a => ({ value: a._id, label: `${a.symbol} — ${a.name}` }))}
            notFoundContent="No unlinked RWA assets"
          />
        </Space>
      </Modal>
    </>
  );
}
